import React from 'react';
import {useTranslation} from 'react-i18next';

import chunk from 'lodash/chunk';

import {Icon, Stack} from '@edna/components/primitives';

import {EContentType} from 'src/constants';
import {
  EMessageMatcherButtonTypes,
  TMaxBotContent,
} from 'src/containers/MessageMatchers/definitions';
import {ExternalLinkIcon, LocationIcon, MaxFileIcon, ProfileIcon} from 'src/icons';
import {MapImage} from 'src/images';

import * as SS from '../style';
import {FOOTER_TIME} from '../utils';
import * as S from './style';

type TProps = {
  content: TMaxBotContent;
  contentType: EContentType;
  children: React.ReactNode;
};

const MAX_LIST_BUTTONS_COUNT = 5;
const BUTTONS_CHUNK_COUNT = 3;

const BUTTON_TYPE_ICON_HASH = {
  [EMessageMatcherButtonTypes.CONTACT]: ProfileIcon,
  [EMessageMatcherButtonTypes.URL]: ExternalLinkIcon,
  [EMessageMatcherButtonTypes.LOCATION]: LocationIcon,
} as Record<EMessageMatcherButtonTypes, TSvgComponentType>;

const ButtonsContent = React.memo<{buttons: TMaxBotContent['buttons']}>(({buttons = []}) => {
  if (buttons.length <= MAX_LIST_BUTTONS_COUNT) {
    return (
      <S.ButtonsList>
        {buttons.map((button, index) => (
          <S.Button key={index}>
            {button.text}
            {BUTTON_TYPE_ICON_HASH[button.buttonType as EMessageMatcherButtonTypes] && (
              <S.ButtonTypeIcon
                as={BUTTON_TYPE_ICON_HASH[button.buttonType as EMessageMatcherButtonTypes]}
              />
            )}
          </S.Button>
        ))}
      </S.ButtonsList>
    );
  }

  return (
    <Stack isFullWidth size="05">
      {chunk(buttons, BUTTONS_CHUNK_COUNT).map((list, index) => (
        <Stack direction="ROW" isFullWidth size="05" key={index}>
          {list.map((button, buttonIndex) => (
            <S.Button key={buttonIndex}>
              {button.text}
              {BUTTON_TYPE_ICON_HASH[button.buttonType as EMessageMatcherButtonTypes] && (
                <S.ButtonTypeIcon
                  as={BUTTON_TYPE_ICON_HASH[button.buttonType as EMessageMatcherButtonTypes]}
                />
              )}
            </S.Button>
          ))}
        </Stack>
      ))}
    </Stack>
  );
});

ButtonsContent.displayName = 'ButtonsContent';

export const MaxContent = React.memo<TProps>(({content, children, contentType}) => {
  const {t} = useTranslation();

  const buttons = content.buttons?.filter((button) => button.text) ?? [];
  const hasButtons = !!buttons.length;
  const attachment = content.attachment?.fileUrl;

  const hasFile = [EContentType.AUDIO, EContentType.DOCUMENT].includes(contentType) && !!attachment;
  const hasImage = contentType === EContentType.IMAGE && !!attachment;
  const hasVideo = contentType === EContentType.VIDEO && !!attachment;
  const isSingleMedia = (hasImage || hasVideo) && !children;

  if (contentType === EContentType.LOCATION) {
    return (
      <S.LocationWrapper>
        <S.Image src={MapImage} />
        <S.Message>
          <S.Button>{t('MessagePreview:openMap')}</S.Button>
          <SS.Time>
            <span>{FOOTER_TIME}</span>
          </SS.Time>
        </S.Message>
      </S.LocationWrapper>
    );
  }

  if (isSingleMedia) {
    return (
      <S.Wrapper>
        <S.Media>
          {hasImage && <S.Image src={attachment} isSingle />}
          {hasVideo && <S.Video url={attachment} isSingle height="auto" width="100%" />}
          <S.MediaTime>
            <span>{FOOTER_TIME}</span>
          </S.MediaTime>
        </S.Media>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      {hasImage && <S.Image src={attachment} />}
      {hasVideo && <S.Video url={attachment} height="auto" width="100%" />}
      <S.Message hasButtons={hasButtons} hasMedia={hasImage || hasVideo}>
        {hasFile && (
          <Stack direction="ROW" size="3" align="center">
            <Icon as={MaxFileIcon} size="32px" />
            <Stack>
              <S.AttachmentLabel size="S">{content.attachment?.originalFileName}</S.AttachmentLabel>
              {!!content.attachment?.size && (
                <S.AttachmentLabel color="grey400" size="S">
                  {content.attachment?.size / 1024} {t('MessagePreview:kilobytes')}
                </S.AttachmentLabel>
              )}
            </Stack>
          </Stack>
        )}
        {contentType !== EContentType.AUDIO && children}
        <SS.Time>
          <span>{FOOTER_TIME}</span>
        </SS.Time>
      </S.Message>
      {hasButtons && <ButtonsContent buttons={buttons} />}
    </S.Wrapper>
  );
});

MaxContent.displayName = 'MaxContent';
